// @flow

import React, { PureComponent } from 'react';
import { graphql, QueryRenderer } from 'react-relay';
import styled from 'styled-components';
import moment from 'moment';
import { i18n } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Page from 'main-app/components/Page';
import PageHeader from 'main-app/components/PageHeader';
import Button from 'shared/components/common/Button';
import * as Actions from 'main-app/store/Actions';
import UpdateReturnOrderMutation from 'main-app/mutations/UpdateReturnOrder';
import ReceiveReturnOrderMutation from 'main-app/mutations/ReceiveReturnOrder';
import EmailReturnOrderMutation from 'main-app/mutations/EmailReturnOrder';
import ReturnOrderStatusPill from 'shared/components/common/Pill/ReturnOrderStatusPill';
import type { RouterHistory, Match } from 'react-router';

const Content = styled.div`
  padding: 24px;
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #666;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 16px;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const DetailLabel = styled.span`
  font-size: 12px;
  color: #999;
  margin-bottom: 4px;
`;

const DetailValue = styled.span`
  font-size: 14px;
  color: #333;
`;

const ActionBar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
`;

const ItemsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 12px;
  border-bottom: 2px solid #e8e8e8;
  font-weight: 600;
  color: #666;
  font-size: 12px;
  text-transform: uppercase;
`;

const ThRight = styled(Th)`
  text-align: right;
`;

const Td = styled.td`
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
  color: #333;
`;

const TdRight = styled(Td)`
  text-align: right;
`;

const ConditionPill = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  background: ${props => {
    switch (props.condition) {
      case 'NEW':
      case 'GOOD':
        return '#e6fffb';
      case 'DAMAGED':
      case 'DEFECTIVE':
        return '#fff1f0';
      case 'UNSALVAGEABLE':
        return '#f5f5f5';
      default:
        return '#f5f5f5';
    }
  }};
  color: ${props => {
    switch (props.condition) {
      case 'NEW':
      case 'GOOD':
        return '#13c2c2';
      case 'DAMAGED':
      case 'DEFECTIVE':
        return '#f5222d';
      case 'UNSALVAGEABLE':
        return '#999';
      default:
        return '#999';
    }
  }};
`;

type Props = {
  history: RouterHistory,
  match: Match,
};

type State = {};

class ReturnOrder extends PureComponent<Props, State> {
  state = {};

  handleAuthorize = (returnOrderId: string) => {
    UpdateReturnOrderMutation.commit({
      variables: { input: { id: returnOrderId, status: 'AUTHORIZED' } },
    })
      .then(() => {
        Actions.alertNotification(i18n.t('Return authorized.'), 'success');
      })
      .catch(() => {
        Actions.alertNotification(i18n.t('Error authorizing return.'), 'error');
      });
  };

  handleReject = (returnOrderId: string) => {
    UpdateReturnOrderMutation.commit({
      variables: { input: { id: returnOrderId, status: 'REJECTED' } },
    })
      .then(() => {
        Actions.alertNotification(i18n.t('Return rejected.'), 'success');
      })
      .catch(() => {
        Actions.alertNotification(i18n.t('Error rejecting return.'), 'error');
      });
  };

  handleReceive = (returnOrder: Object) => {
    const lineEdges = returnOrder.lines?.edges || [];
    const lines = lineEdges.map(edge => ({
      returnOrderLineId: edge.node.id,
      receivedQuantity: edge.node.quantity,
      condition: edge.node.condition,
      disposition: edge.node.disposition,
    }));

    ReceiveReturnOrderMutation.commit({
      variables: { input: { id: returnOrder.id, lines } },
    })
      .then(() => {
        Actions.alertNotification(i18n.t('Return marked as received.'), 'success');
      })
      .catch(() => {
        Actions.alertNotification(i18n.t('Error receiving return.'), 'error');
      });
  };

  handleClose = (returnOrderId: string) => {
    UpdateReturnOrderMutation.commit({
      variables: { input: { id: returnOrderId, status: 'CLOSED' } },
    })
      .then(() => {
        Actions.alertNotification(i18n.t('Return closed.'), 'success');
      })
      .catch(() => {
        Actions.alertNotification(i18n.t('Error closing return.'), 'error');
      });
  };

  handleEmail = (returnOrderId: string) => {
    EmailReturnOrderMutation.commit({
      variables: { input: { returnOrderId } },
    })
      .then(() => {
        Actions.alertNotification(i18n.t('Return authorization email sent.'), 'success');
      })
      .catch(() => {
        Actions.alertNotification(i18n.t('Error sending email.'), 'error');
      });
  };

  formatEnumLabel = (value: ?string): string => {
    if (!value) return '—';
    return value.replace(/_/g, ' ');
  };

  render() {
    const { match, history } = this.props;

    return (
      <QueryRenderer
        environment={relayEnvironment}
        cacheConfig={{ force: true }}
        query={graphql`
          query ReturnOrderQuery($id: ID) {
            returnOrder(id: $id) {
              id
              returnNumber
              status
              returnType
              reason
              authorizedDate
              receivedDate
              notes
              customerNotes
              order {
                id
                orderNumber
              }
              customer {
                id
                name
              }
              shipment {
                id
                shipmentNumber
              }
              lines(first: 1000) {
                edges {
                  node {
                    id
                    quantity
                    receivedQuantity
                    condition
                    disposition
                    restocked
                    notes
                    item {
                      id
                      name
                      itemNumber
                    }
                  }
                }
              }
              updatedAt
              createdAt
            }
            viewer {
              id
              permissions
            }
          }
        `}
        variables={{ id: match.params.id }}
        render={({ props }) => {
          if (!props) return <Page><Loader /></Page>;

          const { returnOrder: ro, viewer } = props;
          const lineEdges = ro.lines?.edges || [];
          const isRequested = ro.status === 'REQUESTED';
          const isAuthorized = ro.status === 'AUTHORIZED';
          const isActive = isRequested || isAuthorized;

          const contextMenuItems = [];
          if (isActive) {
            contextMenuItems.push({
              label: i18n.t('Close Return'),
              onClick: () => this.handleClose(ro.id),
              destructive: true,
            });
          }
          if (isRequested) {
            contextMenuItems.push({
              label: i18n.t('Reject Return'),
              onClick: () => this.handleReject(ro.id),
              destructive: true,
            });
          }

          return (
            <Page>
              <PageHeader
                title={`${i18n.t('Return')} ${ro.returnNumber}`}
                backLabel={i18n.t('Returns')}
                backTo="/returns"
                contextMenuItems={contextMenuItems}
              />
              <Content>
                <ActionBar>
                  {isRequested && (
                    <Button
                      theme="blue"
                      onClick={() => this.handleAuthorize(ro.id)}
                    >
                      {i18n.t('Authorize')}
                    </Button>
                  )}
                  {isAuthorized && (
                    <Button
                      theme="blue"
                      onClick={() => this.handleReceive(ro)}
                    >
                      {i18n.t('Mark Received')}
                    </Button>
                  )}
                  <Button
                    theme="blue"
                    onClick={() => this.handleEmail(ro.id)}
                  >
                    {i18n.t('Email Authorization')}
                  </Button>
                  {isActive && (
                    <Button
                      theme="border-grey"
                      onClick={() => this.handleClose(ro.id)}
                    >
                      {i18n.t('Close Return')}
                    </Button>
                  )}
                </ActionBar>

                <Section>
                  <SectionTitle>{i18n.t('Details')}</SectionTitle>
                  <DetailGrid>
                    <DetailItem>
                      <DetailLabel>{i18n.t('RMA #')}</DetailLabel>
                      <DetailValue>{ro.returnNumber}</DetailValue>
                    </DetailItem>
                    {ro.order && (
                      <DetailItem>
                        <DetailLabel>{i18n.t('Order')}</DetailLabel>
                        <DetailValue>
                          <a
                            href="#"
                            onClick={e => {
                              e.preventDefault();
                              history.push(`/order/${ro.order.id}`);
                            }}
                          >
                            {ro.order.orderNumber}
                          </a>
                        </DetailValue>
                      </DetailItem>
                    )}
                    <DetailItem>
                      <DetailLabel>{i18n.t('Customer')}</DetailLabel>
                      <DetailValue>{ro.customer.name}</DetailValue>
                    </DetailItem>
                    {ro.shipment && (
                      <DetailItem>
                        <DetailLabel>{i18n.t('Shipment')}</DetailLabel>
                        <DetailValue>
                          <a
                            href="#"
                            onClick={e => {
                              e.preventDefault();
                              history.push(`/shipment/${ro.shipment.id}`);
                            }}
                          >
                            {ro.shipment.shipmentNumber}
                          </a>
                        </DetailValue>
                      </DetailItem>
                    )}
                    <DetailItem>
                      <DetailLabel>{i18n.t('Status')}</DetailLabel>
                      <DetailValue>
                        <ReturnOrderStatusPill status={ro.status} />
                      </DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Return Type')}</DetailLabel>
                      <DetailValue>{this.formatEnumLabel(ro.returnType)}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Reason')}</DetailLabel>
                      <DetailValue>{this.formatEnumLabel(ro.reason)}</DetailValue>
                    </DetailItem>
                    {ro.authorizedDate && (
                      <DetailItem>
                        <DetailLabel>{i18n.t('Authorized Date')}</DetailLabel>
                        <DetailValue>
                          {moment(ro.authorizedDate).format('MM/DD/YYYY')}
                        </DetailValue>
                      </DetailItem>
                    )}
                    {ro.receivedDate && (
                      <DetailItem>
                        <DetailLabel>{i18n.t('Received Date')}</DetailLabel>
                        <DetailValue>
                          {moment(ro.receivedDate).format('MM/DD/YYYY')}
                        </DetailValue>
                      </DetailItem>
                    )}
                    <DetailItem>
                      <DetailLabel>{i18n.t('Created')}</DetailLabel>
                      <DetailValue>
                        {moment(ro.createdAt).format('MM/DD/YYYY')}
                      </DetailValue>
                    </DetailItem>
                    {ro.notes && (
                      <DetailItem>
                        <DetailLabel>{i18n.t('Notes')}</DetailLabel>
                        <DetailValue>{ro.notes}</DetailValue>
                      </DetailItem>
                    )}
                    {ro.customerNotes && (
                      <DetailItem>
                        <DetailLabel>{i18n.t('Customer Notes')}</DetailLabel>
                        <DetailValue>{ro.customerNotes}</DetailValue>
                      </DetailItem>
                    )}
                  </DetailGrid>
                </Section>

                <Section>
                  <SectionTitle>{i18n.t('Return Lines')}</SectionTitle>
                  <ItemsTable>
                    <thead>
                      <tr>
                        <Th>{i18n.t('Item')}</Th>
                        <Th>{i18n.t('Item #')}</Th>
                        <ThRight>{i18n.t('Qty')}</ThRight>
                        <ThRight>{i18n.t('Received')}</ThRight>
                        <Th>{i18n.t('Condition')}</Th>
                        <Th>{i18n.t('Disposition')}</Th>
                        <Th>{i18n.t('Restocked')}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineEdges.map(edge => {
                        const line = edge.node;
                        return (
                          <tr key={line.id}>
                            <Td>{line.item.name}</Td>
                            <Td>{line.item.itemNumber}</Td>
                            <TdRight>{line.quantity}</TdRight>
                            <TdRight>{line.receivedQuantity}</TdRight>
                            <Td>
                              {line.condition ? (
                                <ConditionPill condition={line.condition}>
                                  {this.formatEnumLabel(line.condition)}
                                </ConditionPill>
                              ) : '—'}
                            </Td>
                            <Td>{this.formatEnumLabel(line.disposition)}</Td>
                            <Td>{line.restocked ? i18n.t('Yes') : i18n.t('No')}</Td>
                          </tr>
                        );
                      })}
                      {lineEdges.length === 0 && (
                        <tr>
                          <Td colSpan={7} style={{ textAlign: 'center', color: '#999' }}>
                            {i18n.t('No return lines')}
                          </Td>
                        </tr>
                      )}
                    </tbody>
                  </ItemsTable>
                </Section>
              </Content>
            </Page>
          );
        }}
      />
    );
  }
}

export default ReturnOrder;
